import * as jsYaml from 'js-yaml';
import * as unidiff from 'unidiff';
import { removeNonConfigFields } from '../../../common/WorkflowUtils';
import WindsorApi from '../../../api/WindsorApi';
import { parseDiff, tokenize } from 'react-diff-view';
import refractor from 'refractor';

const diffTokenOptions = {
    highlight: true,
    refractor: refractor,
    language: 'yaml',
    oldSource: null,
    enhancers: [],
};

class DiffBuilder {
    private windsorApi: WindsorApi;

    constructor(windsorApi: WindsorApi) {
        this.windsorApi = windsorApi;
    }

    build(approval, execution) {
        return approval.ExecutionPlan !== 'TEARDOWN_ALL' ? this.buildDiffForDeploy(execution) : this.buildDiffForTeardown(execution);
    }

    buildDiffForTeardown(currentExecution) {
        // Generate workflow diff
        const currentWorkflow = jsYaml.dump(removeNonConfigFields(currentExecution['WorkflowCache']), {
            'sortKeys': true
        });
        const workflowDiff = unidiff.formatLines(unidiff.diffLines(currentWorkflow, ''), { context: 3 });

        const diffPromises = currentExecution.WorkflowCache.DeploymentRecipe.map(deployment => {
            return this._getRemovedDiff(deployment);
        });

        return Promise.all(diffPromises).then(results => {
            return {
                workflow: this._createFileData(
                    'workflow',
                    'removed',
                    `Workflow`,
                    workflowDiff
                ),
                actions: results.map(({ diffType, diff, action }) => this.buildActionDiff(action, diffType, diff))
            };
        });
    }

    async buildDiffForDeploy(currentExecution) {
        const lastApprovedExecution = currentExecution.ApprovalRecord.LastApprovedGUID !== '0' ? await this.windsorApi.getExecution(currentExecution.ApprovalRecord.FamilyGUID, currentExecution.ApprovalRecord.LastApprovedGUID) : null;

        const prevExecutionActionMap = lastApprovedExecution ? lastApprovedExecution.WorkflowCache.DeploymentRecipe.reduce((accumulator, action) => {
            accumulator[action.ActionName] = action;
            return accumulator;
        }, {}) : {};

        const currentExecutionActionMap = currentExecution.WorkflowCache.DeploymentRecipe.reduce((accumulator, action) => {
            accumulator[action.ActionName] = action;
            return accumulator;
        }, {});


        const diffPromises = [];

        // Generate workflow diff
        const currentWorkflow = jsYaml.dump(removeNonConfigFields(currentExecution['WorkflowCache']), {
            'sortKeys': true
        });
        const prevWorkflow = lastApprovedExecution ? jsYaml.dump(removeNonConfigFields(lastApprovedExecution['WorkflowCache']), {
            'sortKeys': true
        }) : '';
        const workflowDiff = unidiff.formatLines(unidiff.diffLines(prevWorkflow, currentWorkflow), { context: 3 });


        Object.keys(prevExecutionActionMap).forEach(actionName => {
            const prevAction = prevExecutionActionMap[actionName];
            const currAction = currentExecutionActionMap[actionName];
            if (!currAction) {
                diffPromises.push(this._getRemovedDiff(prevAction));
            } else if (prevAction.Version !== currAction.Version) {
                if (prevAction.DeckName !== currAction.DeckName) {
                    diffPromises.push(this._getRemovedDiff(prevAction));
                    diffPromises.push(this._getAddedDiff(currAction));
                } else {
                    diffPromises.push(this._getChangedDiff(prevAction, currAction));
                }
            }
        });

        Object.keys(currentExecutionActionMap).forEach(actionName => {
            if (!prevExecutionActionMap[actionName]) {
                const action = currentExecutionActionMap[actionName];
                diffPromises.push(this._getAddedDiff(action));
            }
        });

        return Promise.all(diffPromises).then(results => {
            return {
                workflow: this._createFileData(
                    'workflow',
                    lastApprovedExecution ? 'changed' : 'added',
                    `Workflow`,
                    workflowDiff
                ),
                actions: results.map(({ diffType, diff, action }) => this.buildActionDiff(action, diffType, diff))
            };
        });
    }

    buildActionDiff(action: any, diffType: string, diff: any) {
        const actionNode = { 
            actionName: action.ActionName, 
            deckName: action.DeckName,
            deckVersion: action.Version,
            diffType,
            deck: this._createFileData(
                `${action.ActionName}:deckspec`,
                diffType,
                `Deckspec`,
                diff.Diff ? diff.Diff.join('') : ''
            ),
            cloudFormation: [],
            files: []
        };

        Object.keys(diff.CloudFormationsDiff).forEach(changeType => {
            diff.CloudFormationsDiff[changeType].forEach(change => {
                actionNode.cloudFormation.push(this._createFileData(
                    `${action.ActionName}:cloudformation:${change.CloudFormation}`,
                    diffType,
                    change.CloudFormation,
                    change.Diff ? change.Diff.join('') : '',
                    changeType === 'Changed' ? change.Version2.Path : changeType === 'New' ? change.Version1.Path : change.Version2.Path
                ));
            });
        });

        Object.keys(diff.FilesDiff).forEach(changeType => {
            diff.FilesDiff[changeType].forEach(change => {
                actionNode.files.push(this._createFileData(
                    `${action.ActionName}:file:${change.FileName}`,
                    diffType,
                    change.FileName,
                    change.Diff ? change.Diff.join('') : '',
                    change.FileName
                ));
            });
        });

        return actionNode;
    }

    _getChangedDiff(prevAction, currAction) {
        return this.windsorApi.getFaroDeckDiff(currAction.DeckName, currAction.Version, prevAction.Version).then(diff => ({ diffType: 'changed', action: currAction, diff }));
    }

    _getAddedDiff(action) {
        return this.windsorApi.getFaroDeckDiff(action.DeckName, action.Version, undefined).then(diff => ({ diffType: 'added', action, diff }));
    }

    _getRemovedDiff(action) {
        return this.windsorApi.getFaroDeckDiff(action.DeckName, undefined, action.Version).then(diff => ({ diffType: 'removed', action, diff }));
    }

    _createFileData(id, diffType, filename, diff, relativePath = '') {
        return {
            id,
            diffType,
            filename,
            diff: this._parseDiffText(diff),
            relativePath
        }
    }

    _parseDiffText(diffText) {
        const segments = [
            'diff --git a/a b/b',
            `index 3926b0c..4ded779 100644`,
            diffText
        ];

        const normalizedDiff = segments.join('\n');
        const files = parseDiff(normalizedDiff, {});

        const tokens = tokenize(files[0].hunks, diffTokenOptions);

        return { files, tokens };
    }
}

export default DiffBuilder;